/**
 * Pure helpers for filtering the RTS section of the unassigned-projects panel
 * (#136 — RTS count drifted from rendered list when Quick Search was active).
 *
 * Background: `UnassignedProjects.js` had two independent code paths for the
 * RTS section — one that computed the parenthesized count from `scheduleProjects`
 * filtered by status (no query), and another that rendered the actual tiles
 * with the query applied. The two paths drifted: a query that matched zero
 * RTS jobs would empty the rendered list while leaving the count at the
 * unfiltered total.
 *
 * The fix routes BOTH count and render through `filterRtsProjects` so they
 * are structurally guaranteed to agree.
 *
 * Suppressed and Created/Verified sections already use a single useState array
 * for both count and render — they're not affected by this bug. They COULD
 * use the shared `matchesProjectQuery` helper to dedupe the matcher logic,
 * but that's a separate (low-priority) cleanup.
 */

/**
 * Match a single project against the Quick Search query.
 *
 * Matching rule mirrors the existing inline implementations in
 * UnassignedProjects.js: case-insensitive prefix match against any of:
 *   - lastName
 *   - firstName
 *   - invoiceNo (stringified)
 *   - projectNo
 *   - cellPhone
 *
 * Prefix-match semantics are intentional (consistent with how the user
 * encountered the bug — they typed "Holt" and it matched names starting
 * with "Holt", not names containing "Holt"). Empty query matches every
 * project.
 *
 * @param {object} project  must have firstName/lastName/invoiceNo/projectNo/cellPhone
 * @param {string} query    raw search input
 * @returns {boolean}
 */
export function matchesProjectQuery(project, query) {
  if (typeof query !== "string" || query.length === 0) return true;
  if (project == null || typeof project !== "object") return false;

  const queryLength = query.length;
  const querySlice = query.toLowerCase().slice(0, queryLength);

  const invoiceNoSlice =
    project.invoiceNo != null
      ? project.invoiceNo.toString().slice(0, queryLength)
      : undefined;
  const projectNoSlice =
    typeof project.projectNo === "string"
      ? project.projectNo.slice(0, queryLength)
      : undefined;
  const phoneNoSlice =
    typeof project.cellPhone === "string"
      ? project.cellPhone.slice(0, queryLength)
      : undefined;
  const firstnameSlice =
    typeof project.firstName === "string"
      ? project.firstName.toLowerCase().slice(0, queryLength)
      : undefined;
  const lastnameSlice =
    typeof project.lastName === "string"
      ? project.lastName.toLowerCase().slice(0, queryLength)
      : undefined;

  return (
    querySlice === lastnameSlice ||
    querySlice === firstnameSlice ||
    querySlice === invoiceNoSlice ||
    querySlice === projectNoSlice ||
    querySlice === phoneNoSlice
  );
}

/**
 * Returns the filtered RTS-section project list for the unassigned-projects
 * panel. Single source of truth for both the parenthesized count AND the
 * rendered tile list — eliminates the drift that caused #136.
 *
 * Filter rules:
 *   - `start === ""` (the project is in the unassigned bucket)
 *   - `parseFloat(statusCode) === 3` (Ready-To-Schedule)
 *   - if a query is supplied: ALSO must match `matchesProjectQuery`
 *
 * Sorting is intentionally NOT applied here — render call sites apply their
 * own sort (no-query path sorts by dateDue descending; query-active path
 * does not). Keep this pure-filter function focused on the filter contract.
 *
 * @param {Array<object>} scheduleProjects
 * @param {string} query   raw search input ("" or empty string = no query filter)
 * @returns {Array<object>}
 */
export function filterRtsProjects(scheduleProjects, query) {
  if (!Array.isArray(scheduleProjects)) return [];
  return scheduleProjects.filter((project) => {
    if (project == null || typeof project !== "object") return false;
    if (project.start !== "") return false;
    if (parseFloat(project.statusCode) !== 3) return false;
    return matchesProjectQuery(project, query);
  });
}
